import { useCallback } from 'react';
import { Button, Heading, List } from 'yoso';
import { useLogin } from '~/components/Auth';
import { useEmbeddedViewer } from '~/components/Embedded/Embedded.hooks';
import { Translation } from '~/components/I18n';
import { Chevron } from '~/components/Icon/v2/Chevron';
import { LandCard } from '~/components/Land/Card/LandCard';
import { LandEvent, useLandEvent } from '~/components/Land/Event';
import { useMember } from '~/components/Member/Member.hooks';
import { isVaultIosDevice } from '~/components/Util/ua';
import { registerW8benUrl } from '~/components/Wallet/W8ben/W8ben.hooks';
import styles from './LandCardList.module.scss';

export interface LandCardListProps {
  id: LandEvent['week'];
}

export const LandCardList = ({ id }: LandCardListProps) => {
  const { open } = useEmbeddedViewer();
  const login = useLogin();

  const {
    data: { isLoggedIn },
  } = useMember();

  const {
    data: { landList, status, week },
  } = useLandEvent(id);

  const handleW8benLinkClick = useCallback(() => {
    if (!isLoggedIn) {
      login();
      return;
    }

    open(registerW8benUrl);
  }, [isLoggedIn, login, open]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Heading className={styles.title} level={4}>
          {status === 'IN_PROGRESS' ? (
            <Translation name="citizen_land_list_title" />
          ) : (
            <Translation name="citizen_land_list_upcoming_title" options={[week]} />
          )}
        </Heading>
        <p className={styles.description}>
          <Translation balance name="citizen_land_list_desc" />
          {isVaultIosDevice && (
            <>
              <br />
              <Translation name="citizen_app_apple_warning" />
            </>
          )}
        </p>
        <p className={styles.subDescription}>
          <Translation name="citizen_land_list_w8ben_desc" />
          <Button
            as="span"
            variant="text"
            className={styles.w8benLink}
            onClick={handleW8benLinkClick}>
            <Translation name="citizen_button_title_w8ben2" />{' '}
            <Chevron className={styles.icon} direction="right" width={1.5} />
          </Button>
        </p>
      </div>
      <div className={styles.body}>
        <List className={styles.list}>
          {landList.map((item) => (
            <li key={item.landId}>
              <LandCard week={id} {...item} />
            </li>
          ))}
        </List>
      </div>
    </div>
  );
};
